import React, { Component, Fragment } from "react"
import ReactPlayer from "react-player/lazy"
import parse from "html-react-parser"
import { createPopper } from "@popperjs/core"
import { isIOSDevice } from "helpers/util"

import CheckBox from "components/checkbox"
import FormRadio from "components/form/radio"
import FormInput from "components/form/input"
import Button from "components/common/Button"

import "styles/components/common/interactive-video.scss"
import { isEmpty, isEqual } from "lodash"

class InteractiveMediaPlayer extends Component {
  state = {
    url: null,
    pip: false,
    playing: false,
    controls: false,
    light: false,
    volume: 0.8,
    muted: false,
    played: 0,
    loaded: 0,
    duration: 0,
    elapsed: 0,
    remaining: 0,
    seconds: 0,
    playedSeconds: 0,
    playbackRate: 1.0,
    loop: false,
    onError: false,
    isStopped: false,
    questionActive: false,
    questionDetails: {},
    fitbContent: [],
    answerSheet: {},
    fieldErrors: {},
    questionsDone: {},
  }

  componentDidUpdate(prevProps) {
    const { sessionPaused: prevSessionPaused = false } = prevProps
    const { sessionPaused = false } = this.props

    const { playing, isStopped } = this.state

    if (sessionPaused !== prevSessionPaused) {
      this.setState({
        playing: isStopped ? !sessionPaused : false,
        isStopped: playing,
      })
    }
  }

  componentDidMount() {
    this.initializeEvents()
    this.checkProgress()
  }

  componentWillUnmount() {
    this.unInitializeEvents()
  }

  initializeEvents() {
    window.addEventListener("resize", (e) => this.onResize())
  }

  unInitializeEvents() {
    window.removeEventListener("resize", (e) => this.onResize())
  }

  exitFullScreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    }
  }

  onResize() {
    let container = document.querySelector("#player")

    if (container) {
      let rect = container.getBoundingClientRect()

      this.setState({
        overlayHeight: rect.height,
        overlayWidth: rect.width,
      })
    }
  }

  isCorrect(id) {
    const { questionDetails = {}, answerSheet = {} } = this.state
    const answer = answerSheet[id]
    let isCorrect = false

    if (Array.isArray(answer)) {
      if (questionDetails?.type === "fitb") {
        isCorrect = isEqual(questionDetails?.correct_answer, answer)
      } else {
        isCorrect = isEqual(
          questionDetails?.correct_answer.sort(),
          answer.sort()
        )
      }
    } else {
      isCorrect = questionDetails?.correct_answer?.includes(answerSheet[id])
    }

    return isCorrect
  }

  checkProgress = () => {
    const { videoQuestions = [], lastProgress } = this.props
    const { questionsDone = {}, answerSheet = {} } = this.state

    if (!isEmpty(videoQuestions)) {
      videoQuestions.forEach((item) => {
        if (item.timestamp < lastProgress) {
          answerSheet[item.id] = item.correct_answer
          questionsDone[item.id] = true
        }
      })

      this.setState({
        answerSheet,
        questionsDone,
      })
    }
  }

  checkVideoQuestion = async (currentTimestamp) => {
    const { videoQuestions = [] } = this.props
    const { questionsDone = {} } = this.state

    let hasQuestion = false

    if (!isEmpty(videoQuestions)) {
      let question = {}

      question = videoQuestions.find(
        (item) =>
          Math.floor(currentTimestamp) === item.timestamp ||
          (currentTimestamp >= item.timestamp && !questionsDone[item.id])
      )

      if (!isEmpty(question) && !this.state.questionActive) {
        this.player.seekTo(question.timestamp)
        this.exitFullScreen()

        let data = {
          questionActive: true,
          questionDetails: question,
          playing: false,
        }

        if (question.type === "fitb") {
          data["fitbContent"] = this.parseFitbContent(question)
        }

        hasQuestion = true

        await this.setState(data)
        await this.createPopover()
      }
    }

    return hasQuestion
  }

  createPopover = () => {
    const reference = document.querySelector("#vid-question-ref")
    const popover = document.querySelector("#popover")

    createPopper(reference, popover, {
      placement: "bottom",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
        {
          name: "flip",
          options: {
            fallbackPlacements: ["bottom", "right"],
          },
        },
      ],
    })
  }

  parseFitbContent = (data = {}) => {
    const contentArray = data.content?.split("_blank_")
    const contentList = []

    contentArray.forEach((item, idx) => {
      if (item !== "") {
        contentList.push({
          type: "content",
          value: item,
        })
      }

      if (idx < contentArray.length - 1) {
        contentList.push({
          type: "answer",
          value: "",
          status: null,
        })
      }
    })

    return contentList
  }

  handlePlayPause = () => {
    const { playing, questionActive } = this.state
    if (!questionActive) {
      this.setState({ playing: !playing })
    }
  }

  handlePlay = () => {
    const { questionActive } = this.state
    if (!questionActive) {
      this.setState({ playing: true })
    }
  }

  handlePause = async () => {
    const { onPauseMedia = false } = this.props
    const { playing } = this.state

    if (playing) {
      await this.setState({ playing: false })

      if (onPauseMedia) {
        await onPauseMedia()
      }
    }
  }

  handleErrorLoading = (error) => {
    const { onError } = this.state
    this.setState({ onError: !onError })
  }

  handleDuration = (duration) => {
    const { type } = this.props
    this.setState({ duration })

    if (type !== "yt") {
      this.handleOnMediaReady()
    }
  }

  handleProgress = async (state) => {
    const { onProgress, onGetMediaProgress, forwardStreaming } = this.props
    const { playedSeconds } = this.state

    const delta = state.playedSeconds - playedSeconds

    if (delta >= 0) {
      if (delta <= 1.2 || forwardStreaming) {
        const hasQuestion = await this.checkVideoQuestion(state?.playedSeconds)

        await this.setState(state)
        if (!hasQuestion && onGetMediaProgress) {
          await onGetMediaProgress(state)
        }
      } else {
        this.player.seekTo(playedSeconds)
      }
    }

    if (onProgress) {
      await onProgress()
    }
  }

  handleOnMediaReady = () => {
    const { lastProgress } = this.props

    if (lastProgress) {
      this.player.seekTo(lastProgress)
      this.setState({
        playedSeconds: lastProgress,
      })
    } else {
      this.player.seekTo(0.0)
    }
    this.onResize()
  }

  handleEnded = () => {
    const { onEndedMedia } = this.props

    this.setState({ playing: this.state.loop })

    if (onEndedMedia) {
      this.props.onEndedMedia()
    }
  }

  handleSeek = async () => {
    const { forwardStreaming } = this.props

    let video = document.getElementsByTagName("video")[0]

    if (!forwardStreaming) {
      video.onseeking = this.onSeeking
    }
  }

  onSeeking = () => {
    const { playedSeconds } = this.state

    let player = false
    try {
      player = document.getElementsByTagName("video")[0]
    } catch (err) {
      player = false
    }

    const targetTime = this.player.getCurrentTime()
    const delta = targetTime - playedSeconds

    if (player && delta > 0.01) {
      player.currentTime = playedSeconds
    }
  }

  submitAnswer = (name) => {
    const { isExamFinished = false } = this.props
    const { questionsDone, questionDetails = {} } = this.state

    if (questionDetails?.type === "info") {
      // TEMPORARY
      questionsDone[name] = true
      this.setState({ questionsDone })
      this.handleNext()
      return
    } else if (questionDetails?.type === "fitb") {
      this.validateFitb(name)
    } else {
      this.validateField(name)
    }

    let fieldErrors = this.state.fieldErrors

    if (
      questionDetails?.type === "fitb" ||
      isEmpty(fieldErrors[name]) ||
      isExamFinished
    ) {
      const remainingRetries = localStorage.getItem("max_attempts")
      if (!this.isCorrect(questionDetails?.id) && remainingRetries <= 1) {
        questionsDone[name] = true
        fieldErrors[name] = "Oops! Sorry that answer is incorrect."
      } else if (!this.isCorrect(questionDetails?.id)) {
        fieldErrors[name] =
          "Oops! Sorry that answer is incorrect. Please try again."
        if (remainingRetries >= 1 && remainingRetries !== "unli") {
          let updatedRemainingRetries = remainingRetries - 1
          localStorage.setItem("max_attempts", updatedRemainingRetries)
        }
      } else {
        questionsDone[name] = true
      }

      this.setState({ questionsDone, fieldErrors })
    }
  }

  formatAnswer = (answer) => {
    if (answer) {
      if (Array.isArray(answer)) {
        if (answer.length > 1) {
          return `- ${answer.join("\n- ")}`
        } else {
          return answer[0]
        }
      } else {
        return answer
      }
    }
  }

  validateField = (name, message) => {
    const { answerSheet } = this.state
    let fieldErrors = this.state.fieldErrors

    if (isEmpty(message)) {
      message = "This field is required"
    }

    if (isEmpty(answerSheet[name])) {
      fieldErrors[name] = message
    } else {
      delete fieldErrors[name]
    }

    this.setState({ fieldErrors })
  }

  validateFitb = (name) => {
    const { questionDetails = {} } = this.state
    let answerSheet = this.state.answerSheet
    let fieldErrors = this.state.fieldErrors
    let fitbContent = this.state.fitbContent || []
    let correctAnswers = questionDetails?.correct_answer || []
    let answerIdx = 0
    let answers = []
    let fieldId = null

    fitbContent.map((item, idx) => {
      if (item.type === "answer") {
        if (item.value === correctAnswers[answerIdx]) {
          item.status = "success"
          answers.push(item.value)
        } else {
          item.status = "error"

          if (!fieldId) {
            fieldId = `${questionDetails.id}-${idx}`
          }
        }
        answerIdx++
      }

      return item
    })

    if (fieldId) {
      const field = document.getElementById(fieldId)

      if (field) {
        field.focus()
      }
    }

    if (isEqual(correctAnswers, answers)) {
      delete fieldErrors[name]
    }

    answerSheet[name] = answers

    this.setState({ fieldErrors, fitbContent, answerSheet })
  }

  handleNext = () => {
    const { maxAttempts } = this.props
    const max_attempts = maxAttempts === 0 ? "unli" : maxAttempts
    localStorage.setItem("max_attempts", max_attempts)
    this.setState({
      playing: true,
      questionActive: false,
      questionDetails: {},
      fitbContent: [],
    })
  }

  handleSelect = (e) => {
    const { answerSheet } = this.state
    const { name, value } = e.currentTarget
    this.setState(
      {
        answerSheet: {
          ...answerSheet,
          [name]: value,
        },
      },
      () => this.validateField(name)
    )
  }

  handleChange = (e) => {
    const { answerSheet } = this.state
    const { name, value } = e.currentTarget
    this.setState(
      {
        answerSheet: {
          ...answerSheet,
          [name]: value,
        },
      },
      () => this.validateField(name)
    )
  }

  handleSelectSingle = (name, value) => {
    const { answerSheet } = this.state
    this.setState(
      {
        answerSheet: {
          ...answerSheet,
          [name]: value,
        },
      },
      () => this.validateField(name)
    )
  }

  handleSelectMultiple = (e) => {
    const { answerSheet } = this.state
    const target = e.currentTarget
    const { value, name } = target

    if (target.checked) {
      if (isEmpty(answerSheet[name])) {
        this.setState(
          {
            answerSheet: {
              ...answerSheet,
              [name]: [value],
            },
          },
          () => this.validateField(name, "Please select an answer")
        )
      } else {
        this.setState(
          {
            answerSheet: {
              ...answerSheet,
              [name]: [...answerSheet[name], value],
            },
          },
          () => this.validateField(name, "Please select an answer")
        )
      }
    } else {
      this.setState(
        {
          answerSheet: {
            ...answerSheet,
            [name]: answerSheet[name].filter((item) => item !== value),
          },
        },
        () => this.validateField(name, "Please select an answer")
      )
    }
  }

  handleFitbChange = (e, idx) => {
    const { fitbContent = [] } = this.state
    const { value } = e.currentTarget
    const newValues = [...fitbContent]

    if (idx >= 0 && !isEmpty(newValues[idx])) {
      newValues[idx].value = value
    }

    this.setState({ fitbContent: newValues })
  }

  formatFitbContent = (data = {}) => {
    const { fitbContent = [] } = this.state
    let renderContent = []

    fitbContent.forEach((item, idx) => {
      if (item.type === "answer") {
        const fieldValue = item.value
        let fieldId = `${data.id}-${idx}`

        renderContent.push(
          <input
            id={fieldId}
            name={fieldId}
            className={`fitb-answer -${item.status}`}
            value={fieldValue}
            size={fieldValue.length > 8 ? fieldValue.length : 8}
            placeholder="Your answer"
            onChange={(e) => this.handleFitbChange(e, idx)}
            readOnly={item.status === "success"}
          />
        )
      } else {
        renderContent.push(<span className="fitb-content">{item.value}</span>)
      }
    })

    return renderContent
  }

  displayRemarks = (data = {}) => {
    const fieldErrors = this.state.fieldErrors
    return (
      <Fragment>
        <div className="video-exam-answer">
          {fieldErrors[data.id] && (
            <>
              <p className="label">Correct Answer </p>
              <p className="answer">{data?.correct_answer?.join(", ")}</p>
            </>
          )}

          {data?.remarks && (
            <>
              <p className="label">Explanation</p>
              <p className="answer">{data?.remarks}</p>
            </>
          )}
        </div>
      </Fragment>
    )
  }

  displayExamType = (data = {}, isDone) => {
    const { answerSheet, fieldErrors, questionDetails } = this.state

    const isCorrect = isDone && this.isCorrect(questionDetails?.id)

    switch (data.type) {
      case "info":
        return <div id="html-container">{parse(questionDetails?.content)}</div>

      case "radio":
        return (
          <Fragment>
            <div id="html-container">{parse(questionDetails?.content)}</div>
            <div className="control video-exam-radio">
              <p className="choices-header">Select your answer</p>
              {data.choices?.map((item, i) => (
                <FormRadio
                  id={i}
                  key={i}
                  name={data.id}
                  checked={
                    answerSheet[data.id] === item ||
                    answerSheet[data.id]?.includes(item)
                  }
                  onChange={() => this.handleSelectSingle(data.id, item)}
                  text={item}
                  disabled={isDone}
                />
              ))}
              {isCorrect && (
                <p className="form-success text-bold">Yes, that's correct!</p>
              )}
              {fieldErrors[data.id] && (
                <p className="error-message">{`${fieldErrors[data.id]}`}</p>
              )}
            </div>
          </Fragment>
        )

      case "multiple":
      case "checkbox":
        return (
          <Fragment>
            <div id="html-container">{parse(questionDetails?.content)}</div>
            <div className="control video-exam-checkbox">
              <p className="choices-header">Select your answer</p>
              <small className="choices-subheader">
                Check all possible answers.
              </small>
              {data.choices?.map((item, i) => (
                <CheckBox
                  name={data.id}
                  value={item}
                  text={item}
                  id={`chk-choice-${data.id}-${i}`}
                  checked={answerSheet[data.id]?.includes(item)}
                  onChange={this.handleSelectMultiple}
                  disabled={isDone}
                />
              ))}
              {isCorrect && (
                <p className="form-success">Yes, that's correct!</p>
              )}
              {fieldErrors[data.id] && (
                <p className="error-message">{`* ${fieldErrors[data.id]}`}</p>
              )}
            </div>
          </Fragment>
        )

      case "dropdown":
      case "select":
        return (
          <Fragment>
            <div className="video-exam-select">
              <p className="choices-header">Select your answer</p>
              <div className="select-wrapper" id="sort">
                <select
                  name={data.id}
                  className="select"
                  onChange={this.handleSelect}
                  value={[answerSheet[data.id]]}
                  disabled={isDone}
                >
                  {data.answer ? (
                    ""
                  ) : (
                    <option value={null} key={-1}>
                      Select answer...
                    </option>
                  )}
                  {data.choices?.map((item, index) => (
                    <option value={item} key={index}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              {isCorrect && (
                <p className="form-success form-hint">Yes, that's correct!</p>
              )}
              {fieldErrors[data.id] && (
                <p className="error-message">{`* ${fieldErrors[data.id]}`}</p>
              )}
            </div>
          </Fragment>
        )

      case "text":
        return (
          <Fragment>
            <div id="html-container">{parse(questionDetails?.content)}</div>
            <div className="video-exam-text">
              <p className="choices-header">Answer</p>
              <FormInput
                id={data.id}
                name={data.id}
                value={answerSheet[data.id]}
                placeholder="Your answer"
                handleChange={this.handleChange}
                disabled={isDone}
              />
              {isCorrect && (
                <p className="form-success text-bold">Yes, that's correct!</p>
              )}
              {fieldErrors[data.id] && (
                <p className="error-message">{`${fieldErrors[data.id]}`}</p>
              )}
            </div>
          </Fragment>
        )

      case "fitb":
        return (
          <Fragment>
            <div className="video-exam-fitb">
              <p className="fitb-container">{this.formatFitbContent(data)}</p>
              {isCorrect && (
                <p className="form-success text-bold">Yes, that's correct!</p>
              )}
            </div>
          </Fragment>
        )

      default:
        break
    }
  }

  renderQuestion = () => {
    const {
      questionActive,
      questionDetails = {},
      overlayHeight,
      overlayWidth,
      questionsDone,
    } = this.state
    const { lmsTheme } = this.props

    if (questionActive) {
      let xCoord = (questionDetails?.location?.x / 100) * overlayWidth
      let yCoord = (questionDetails?.location?.y / 100) * overlayHeight

      const styles = {
        left: `${xCoord}px`,
        top: `${yCoord}px`,
      }

      const isInfoType = questionDetails?.type === "info"
      const isComplete = questionsDone[questionDetails?.id]

      return (
        <div id="interactive-overlay" className="interactive-overlay">
          <div
            id="vid-question-ref"
            className={`question-reference ${lmsTheme}`}
            style={styles}
          >
            {" "}
          </div>
          <div
            id="popover"
            role="tooltip"
            className={`interactive-question ${lmsTheme}`}
          >
            <div className="question-container">
              <div className="content">
                {this.displayExamType(questionDetails, isComplete)}
                {isComplete &&
                  !isInfoType &&
                  this.displayRemarks(questionDetails)}
              </div>
              <div className="actions">
                <Button
                  label={isComplete || isInfoType ? "Continue" : "Submit"}
                  block
                  onClick={() =>
                    isComplete
                      ? this.handleNext()
                      : this.submitAnswer(questionDetails?.id)
                  }
                />
              </div>
            </div>
            <div id="arrow" data-popper-arrow />
          </div>
        </div>
      )
    } else {
      return ""
    }
  }

  ref = (player) => {
    this.player = player
  }

  render() {
    const {
      media,
      type,
      lastProgress,
      mediaConfig,
      forwardStreaming,
      customStyle,
      lmsTheme,
    } = this.props
    const { playing, onError } = this.state

    const iOSDevice = isIOSDevice()

    return (
      <div
        className={`interactive-player-wrapper ${type} ${lmsTheme}`}
        style={customStyle}
      >
        {onError ? (
          <div className="error-media">
            <div className="video-overlay">
              <span className="icon">
                <i className="fas fa-exclamation-triangle fa-3x"></i>
              </span>
              <span className="error-media-label">Unable to load media</span>
            </div>
          </div>
        ) : (
          <Fragment>
            {type && (
              <div className="interactive-container">
                {this.renderQuestion()}
                <ReactPlayer
                  id="player"
                  ref={this.ref}
                  url={media}
                  controls
                  className="react-player"
                  width="100%"
                  height="100%"
                  playing={playing}
                  playsinline={iOSDevice ? true : false}
                  onPlay={this.handlePlay}
                  onPause={this.handlePause}
                  onError={this.handleErrorLoading}
                  onProgress={this.handleProgress}
                  onDuration={this.handleDuration}
                  onEnded={this.handleEnded}
                  onSeek={this.handleSeek}
                  onContextMenu={(e) => e.preventDefault()}
                  config={{
                    file: {
                      attributes: {
                        controlsList: mediaConfig?.video_download
                          ? "noplaybackrate"
                          : "nodownload noplaybackrate",
                        disablePictureInPicture: true,
                      },
                    },
                    youtube: {
                      playerVars: {
                        modestbranding: 1,
                        rel: 0,
                        disablekb: 1,
                        showinfo: 0,
                        start: Math.floor(lastProgress),
                        controls: forwardStreaming ? 1 : 0,
                        fs: iOSDevice ? 0 : 1,
                      },
                    },
                    soundcloud: {
                      options: {
                        auto_play: false,
                        buying: false,
                        sharing: false,
                        download: false,
                        show_playcount: false,
                        show_user: false,
                        single_active: true,
                        start_track: lastProgress,
                      },
                      playerVars: {
                        controls: forwardStreaming ? 1 : 0,
                      },
                    },
                  }}
                />
              </div>
            )}
          </Fragment>
        )}
      </div>
    )
  }
}

export default InteractiveMediaPlayer
